import { ValueObject } from '../shared/ValueObject';
import { ValidationError } from '../errors/ValidationError';

export type LogLevelValue = 'info' | 'warning' | 'error';

/**
 * ログレベル値オブジェクト
 *
 * システムログのレベルを表す
 */
export class LogLevel extends ValueObject<LogLevelValue> {
  private static readonly VALID_LEVELS: readonly LogLevelValue[] = [
    'info',
    'warning',
    'error'
  ];

  // 優先度の定数定義
  private static readonly PRIORITY_INFO = 0;
  private static readonly PRIORITY_WARNING = 1;
  private static readonly PRIORITY_ERROR = 2;

  private static readonly LEVEL_PRIORITY = {
    info: LogLevel.PRIORITY_INFO,
    warning: LogLevel.PRIORITY_WARNING,
    error: LogLevel.PRIORITY_ERROR
  };

  private constructor(value: LogLevelValue) {
    super(value);
  }

  /**
   * LogLevelを生成
   * @throws {ValidationError} 不正な値の場合
   */
  public static create(value: string): LogLevel {
    LogLevel.validate(value);
    return new LogLevel(value as LogLevelValue);
  }

  /**
   * ファクトリメソッド: info レベルを生成
   */
  public static createInfo(): LogLevel {
    return new LogLevel('info');
  }

  /**
   * ファクトリメソッド: warning レベルを生成
   */
  public static createWarning(): LogLevel {
    return new LogLevel('warning');
  }

  /**
   * ファクトリメソッド: error レベルを生成
   */
  public static createError(): LogLevel {
    return new LogLevel('error');
  }

  private static validate(value: string): void {
    if (!LogLevel.VALID_LEVELS.includes(value as LogLevelValue)) {
      throw new ValidationError(
        `無効なログレベル: ${value}. 有効な値: ${LogLevel.VALID_LEVELS.join(', ')}`
      );
    }
  }

  /**
   * infoレベルかどうかを判定
   */
  public isInfo(): boolean {
    return this.value === 'info';
  }

  /**
   * warningレベルかどうかを判定
   */
  public isWarning(): boolean {
    return this.value === 'warning';
  }

  /**
   * errorレベルかどうかを判定
   */
  public isError(): boolean {
    return this.value === 'error';
  }

  /**
   * 優先度を取得（数値が大きいほど優先度が高い）
   */
  public getPriority(): number {
    return LogLevel.LEVEL_PRIORITY[this.value];
  }

  /**
   * 他のLogLevelより優先度が高いかを判定
   */
  public isHigherPriorityThan(other: LogLevel): boolean {
    return this.getPriority() > other.getPriority();
  }

  /**
   * 他のLogLevel以上の優先度かを判定
   */
  public isHigherOrEqualPriorityThan(other: LogLevel): boolean {
    return this.getPriority() >= other.getPriority();
  }

  public toString(): string {
    return this.value;
  }

  public toJSON(): string {
    return this.value;
  }

  /**
   * 日本語表示文字列を取得
   */
  public toDisplayString(): string {
    switch (this.value) {
      case 'info':
        return '情報';
      case 'warning':
        return '警告';
      case 'error':
        return 'エラー';
    }
  }


  /**
   * インデックスアクセス用のキー文字列を取得
   * オブジェクトのキーとして使用する場合に使用
   *
   * @example
   * const config = LOG_LEVEL_CONFIG[logLevel.toKey()];
   */
  public toKey(): string {
    return this.value;
  }

  /**
   * 他のLogLevelと等しいかを判定
   */
  public equals(other: LogLevel | null | undefined): boolean {
    if (!(other instanceof LogLevel)) {
      return false;
    }
    return this.value === other.value;
  }

}